import React, { useEffect, useState } from 'react';
import cookieStore from 'js-cookie';
import { Checkbox, Input, Table, Spin } from 'antd';
import { useHistory } from 'react-router-dom';
import { ReactComponent as LeftArrow } from '../../assets/img/left-arrow.svg';
import { SearchOutlined, SettingFilled } from '@ant-design/icons';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { fetchClaimList, setRowDetails } from '../../store/actions/claimListActions';

const ClaimsList = () => {
  const [claims, setClaims] = useState([]);
  const [filteredValue, setFilteredValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  // function for fetching the claim details -
  const fetchClaimDetails = () => {
    // if setIsLoading is set to true the spin component will render else not
    setIsLoading(true);
    // getting the access token and other required parameters to send as payload in header and api
    const accessToken = cookieStore.get('accessToken') || '';
    const data: any = localStorage.getItem('data');
    const parsedData = JSON.parse(data);
    const payerId = parsedData.providerDetail.providerId;
    const isReimbursementClaim = false;
    const range = 100;
    const startIndex = 1;
    const isSearchEnabled = false;
    const filters = [
      {
        key: 'providerName',
        value: [filteredValue],
      },
    ];
    // payload that needs to be sent in payload -
    const sendData: any = {
      payerId,
      isReimbursementClaim,
      startIndex,
      isSearchEnabled,
      range,
      filters,
    };
    const requestOptions = {
      headers: { 'Content-Type': 'application/json', accesstoken: accessToken },
    };
    axios
      .post(
        `${process.env.REACT_APP_API_BASE_URL}Provider/Search/ClaimsByActionsAll`,
        sendData,
        requestOptions,
      )
      .then((result: any) => {
        setClaims(result?.data?.claimDetails);
        const claimDetails = result?.data?.claimDetails || [];
        dispatch(fetchClaimList(claimDetails));
        setIsLoading(false);
      });
  };

  // useEffect for fetching the claim details so that api gets called upon component mounting
  useEffect(() => {
    fetchClaimDetails();
  }, []);

  // tslint:disable-next-line:max-line-length
  // here in the columns in the hospital name section input search is enabled and also on the press of enter button the search will be triggered
  // also on the click of the search icon the seach will be triggered
  const columns = [
    {
      title: 'Hospital Name',
      dataIndex: 'providerName',
      key: 'providerName',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => {
        return (
          <Input.Search
            autoFocus={true}
            placeholder="Enter Hospital Name"
            value={selectedKeys[0]}
            onChange={(e: any) => {
              setSelectedKeys(e?.target?.value ? [e?.target?.value] : []);
              setFilteredValue(e?.target?.value);
            }}
            onPressEnter={() => {
              fetchClaimDetails();
            }}
            onBlur={() => {
              confirm();
            }}
            onSearch={(value: any) => {
              setFilteredValue(value);
              fetchClaimDetails();
            }}
          />
        );
      },
      filterIcon: () => {
        return <SearchOutlined rev={''} />;
      },
      onFilter: (value: any, record: any) => {
        return record?.providerName
          ?.toLowerCase()
          .includes(value?.toLowerCase());
      },
    },
    {
      title: 'Insurer Name',
      dataIndex: 'insurerName',
      key: 'insurerName',
    },
    {
      title: 'IP No',
      dataIndex: 'inPatientNo',
      key: 'inPatientNo',
    },
    {
      title: 'Member ID',
      dataIndex: 'memberId',
      key: 'memberId',
    },
    {
      title: 'Full Name',
      dataIndex: 'patientName',
      key: 'patientName',
    },
    {
      title: 'Claimed Amount',
      dataIndex: 'claimedAmount',
      key: 'claimedAmount',
    },
    {
      title: 'Status',
      dataIndex: 'statusName',
      key: 'statusName',
    },
    {
      title: 'DOA',
      dataIndex: 'doa',
      key: 'doa',
      render: (date: any) => {
        // converting the date to indian format --> DD/MM/YY and with the removed timestamp
        const formattedDate = new Date(date).toLocaleDateString('en-GB');
        return formattedDate;
      },
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((column: any) => column.key),
  );
  const [showSettings, setShowSettings] = useState(false);

  const handleColumnToggle = (columnKey: any) => {
    if (visibleColumns.includes(columnKey)) {
      setVisibleColumns(visibleColumns.filter((key: any) => key !== columnKey));
    } else {
      setVisibleColumns([...visibleColumns, columnKey]);
    }
  };

  const handleSettingsClick = () => {
    setShowSettings(!showSettings);
  };

  // function to handle the row click in order to go to claim details page -->
  const handleRowClick = (record: any) => {
    const {providerName, payerId, payerClaimNo, providerId, hospActionId} = record;
    dispatch(setRowDetails(payerId, providerId, hospActionId, payerClaimNo, providerName));
    history.push(`/dashboard/claimsDetails/`);
  };

  const renderSettingsIcon = () => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <SettingFilled
        style={{ marginRight: '8px', fontSize: '2rem', color: '#2E2BEB' }}
        onClick={handleSettingsClick}
        rev={''}
      />
      {showSettings && (
        <div style={{ marginLeft: '8px', display: 'flex', flexWrap: 'wrap' }}>
          {columns.map((column: any) => (
            <div key={column.key} style={{ marginLeft: '8px' }}>
              <Checkbox
                checked={visibleColumns.includes(column.key)}
                onChange={() => handleColumnToggle(column.key)}
              >
                {column.title}
              </Checkbox>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const filteredColumns = columns.filter((column: any) =>
    visibleColumns.includes(column.key),
  );

  const history = useHistory();

  const logoutHandler = () => {
    cookieStore.remove('accessToken');
    history.replace('/');
  };

  return (
    <div style={{ padding: '20px' }}>
      <div>
        <button onClick={logoutHandler} className="logout-btn">
          <LeftArrow className="logout-arrow" />
          &nbsp;&nbsp; Logout
        </button>
      </div>
      <Spin spinning={isLoading}>
        <Table
          columns={filteredColumns}
          title={() => renderSettingsIcon()}
          dataSource={claims}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
        />
      </Spin>
    </div>
  );
};

export default ClaimsList;
